import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import type { StickerIconName } from "@ui/components/Icon/icons";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Link } from "@ui/primitives/Link";

const seeAllCard = tv({
  slots: {
    root: "relative flex aspect-square w-full flex-col items-center justify-center gap-2 self-start rounded-3xl md:gap-3",
    iconWrapper: "flex size-[48%] items-center justify-center drop-shadow-lg",
    label: "text-base font-semibold leading-5 md:text-xl md:leading-7 lg:text-2xl lg:leading-8 lg:tracking-[-0.24px]",
    insetBorder: "pointer-events-none absolute inset-0 rounded-[inherit]",
  },
  variants: {
    color: {
      green: {
        root: "bg-green-50",
        label: "text-green-800",
        insetBorder:
          "shadow-[inset_0px_-1px_0px_0px_var(--color-green-200),inset_0px_0px_0px_1px_var(--color-green-100)]",
      },
      pink: {
        root: "bg-pink-50",
        label: "text-pink-700",
        insetBorder:
          "shadow-[inset_0px_-1px_0px_0px_var(--color-pink-200),inset_0px_0px_0px_1px_var(--color-pink-100)]",
      },
      orange: {
        root: "bg-orange-50",
        label: "text-orange-800",
        insetBorder:
          "shadow-[inset_0px_-1px_0px_0px_var(--color-orange-300),inset_0px_0px_0px_1px_var(--color-orange-100)]",
      },
      blue: {
        root: "bg-blue-50",
        label: "text-blue-700",
        insetBorder:
          "shadow-[inset_0px_-1px_0px_0px_var(--color-blue-200),inset_0px_0px_0px_1px_var(--color-blue-100)]",
      },
    },
  },
});

type SeeAllCardVariants = VariantProps<typeof seeAllCard>;

export interface SeeAllCardProps {
  color: NonNullable<SeeAllCardVariants["color"]>;
  href: string;
  icon: StickerIconName;
  label: string;
}

export function SeeAllCard({ color, href, icon, label }: SeeAllCardProps) {
  const styles = seeAllCard({ color });

  return (
    <div className={styles.root()}>
      <div className={styles.iconWrapper()}>
        <div className="rotate-[10deg]">
          <Icon className="size-full" name={icon} type="sticker" />
        </div>
      </div>
      <span className={styles.label()}>{t("components.views.homes.show.see_all")}</span>
      <div className={styles.insetBorder()} />
      <Link aria-label={label} cacheFor="30s" className="absolute inset-0" href={href} prefetch="hover" />
    </div>
  );
}
